import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { api, ApiError } from "@/lib/api";
import { useEffect, useState, startTransition } from "react";
import { analytics } from "@/lib/analytics";

interface Plan {
  id: string;
  name: string;
  domains: number;
  messagesPerMonth: number;
  price: number;
}

interface Subscription {
  plan: string;
  planName: string;
  status: string | null;
  currentPeriodEnd: string | null;
  domainLimit: number;
  messageLimit: number;
  monthlyMessageCount: number;
  usageResetAt: string;
}

export function SettingsPage() {
  const { user, currentOrganization, setOrganizations, organizations } = useAuthStore();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [orgName, setOrgName] = useState(currentOrganization?.name || "");
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const searchParams = new URLSearchParams(window.location.search);
  const billing = searchParams.get("billing");

  const canEditOrg = currentOrganization?.role === "owner" || currentOrganization?.role === "admin";

  const updateOrgMutation = useMutation({
    mutationFn: (name: string) => api.organizations.update(currentOrganization!.id, { name }),
    onSuccess: (updated) => {
      // Update the organizations in the store
      const updatedOrgs = organizations.map((org) =>
        org.id === updated.id ? { ...org, name: updated.name } : org
      );
      setOrganizations(updatedOrgs);
      setIsEditingOrg(false);
      setOrgError(null);
      setSuccessMessage("Organization name updated!");
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setOrgError(err.message);
      } else {
        setOrgError("Failed to update organization");
      }
    },
  });

  useEffect(() => {
    if (billing === "success") {
      analytics.subscriptionStarted("unknown"); // Plan info not available from Stripe redirect
      startTransition(() => {
        setSuccessMessage("Subscription updated successfully!");
      });
      window.history.replaceState({}, "", "/settings");
    }
  }, [billing]);

  useEffect(() => {
    if (currentOrganization) {
      startTransition(() => {
        setOrgName(currentOrganization.name);
      });
    }
  }, [currentOrganization]);

  const { data: plans } = useQuery<Plan[]>({
    queryKey: ["plans"],
    queryFn: () => api.billing.getPlans() as Promise<Plan[]>,
  });

  const { data: subscription } = useQuery<Subscription>({
    queryKey: ["subscription", currentOrganization?.id],
    queryFn: () => api.billing.getSubscription(currentOrganization!.id) as Promise<Subscription>,
    enabled: !!currentOrganization?.id,
  });

  const checkoutMutation = useMutation({
    mutationFn: ({ planId }: { planId: string }) =>
      api.billing.createCheckout(currentOrganization!.id, planId) as Promise<{ url: string }>,
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });

  const portalMutation = useMutation({
    mutationFn: () =>
      api.billing.createPortal(currentOrganization!.id) as Promise<{ url: string }>,
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });

  if (!currentOrganization) {
    return <div>No organization selected</div>;
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const usagePercent = subscription
    ? Math.round((subscription.monthlyMessageCount / subscription.messageLimit) * 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-slate">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative">
          <div className="page-header-content">
            <div className="page-header-icon hidden sm:block">
              <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">Settings</h1>
              <p className="text-white/80 text-sm sm:text-base">Manage your account and organization</p>
            </div>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="alert alert-success">
          <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Profile section */}
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-slate-solid">
            <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">Profile</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg bg-muted/50 p-4">
            <label className="block text-sm font-medium text-muted-foreground">Name</label>
            <div className="mt-1 font-medium">{user?.name}</div>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <label className="block text-sm font-medium text-muted-foreground">Email</label>
            <div className="mt-1 font-medium">{user?.email}</div>
          </div>
        </div>
      </div>

      {/* Organization section */}
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-slate-solid">
            <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">Organization</h2>
        </div>

        {orgError && (
          <div className="alert alert-error mb-4">
            <svg className="icon-md shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            {orgError}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg bg-muted/50 p-4">
            <label className="block text-sm font-medium text-muted-foreground">Name</label>
            {isEditingOrg ? (
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="input input-primary flex-1"
                  placeholder="Organization name"
                />
                <button
                  onClick={() => updateOrgMutation.mutate(orgName)}
                  disabled={updateOrgMutation.isPending || !orgName.trim()}
                  className="btn btn-primary btn-sm"
                >
                  {updateOrgMutation.isPending ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => {
                    setIsEditingOrg(false);
                    setOrgName(currentOrganization.name);
                    setOrgError(null);
                  }}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-1 flex items-center justify-between">
                <span className="font-medium">{currentOrganization.name}</span>
                {canEditOrg && (
                  <button
                    onClick={() => setIsEditingOrg(true)}
                    className="text-sm text-indigo-600 hover:text-indigo-500"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <label className="block text-sm font-medium text-muted-foreground">Your Role</label>
            <div className="mt-1 font-medium capitalize">{currentOrganization.role}</div>
          </div>
        </div>
      </div>

      {/* Usage section */}
      {subscription && (
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-slate-500 to-slate-600">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold">Usage</h2>
              <p className="text-sm text-muted-foreground">Current billing period</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium">Messages this month</span>
                <span className="text-muted-foreground">
                  {formatNumber(subscription.monthlyMessageCount)} / {formatNumber(subscription.messageLimit)}
                </span>
              </div>
              <div className="h-3 rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-3 rounded-full transition-all ${
                    usagePercent >= 90
                      ? "bg-gradient-to-r from-red-500 to-red-600"
                      : usagePercent >= 75
                      ? "bg-gradient-to-r from-yellow-500 to-orange-500"
                      : "bg-gradient-to-r from-indigo-500 to-purple-600"
                  }`}
                  style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
              </div>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Domain limit</span>
                <span className="text-sm text-muted-foreground">{subscription.domainLimit} domains</span>
              </div>
            </div>
            {subscription.status && subscription.status !== "active" && (
              <div className="alert alert-warning">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                Subscription status: {subscription.status}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Plan section */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-slate-500 to-slate-600">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold">Plan</h2>
              <p className="text-sm text-muted-foreground">
                {!subscription
                  ? "Loading..."
                  : subscription.plan === "free"
                  ? "Upgrade to monitor more domains"
                  : `You're on the ${subscription.planName} plan`}
              </p>
            </div>
          </div>
          {subscription?.plan !== "free" && (
            <button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="btn btn-secondary btn-sm w-full sm:w-auto"
            >
              {portalMutation.isPending ? "Loading..." : "Manage Billing"}
            </button>
          )}
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {(plans || []).map((plan) => {
            const isCurrentPlan = subscription?.plan === plan.id;
            const canUpgrade = plan.id !== "free" && !isCurrentPlan;

            return (
              <div
                key={plan.id}
                className={`rounded-xl border p-5 transition-all ${
                  isCurrentPlan
                    ? "border-indigo-500/50 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 ring-1 ring-indigo-500/20"
                    : "hover:border-muted-foreground/30"
                }`}
              >
                <div className="font-semibold">{plan.name}</div>
                <div className="mt-2 text-3xl font-bold">
                  ${plan.price}
                  {plan.price > 0 && (
                    <span className="text-sm font-normal text-muted-foreground">/mo</span>
                  )}
                </div>
                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <svg className="icon-sm text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    {plan.domains} domains
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="icon-sm text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    {formatNumber(plan.messagesPerMonth)} messages/mo
                  </div>
                </div>
                {isCurrentPlan ? (
                  <div className="mt-4 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-2 text-center text-sm font-medium text-white">
                    Current Plan
                  </div>
                ) : canUpgrade ? (
                  <button
                    onClick={() => {
                      analytics.checkoutStarted(plan.name);
                      checkoutMutation.mutate({ planId: plan.id });
                    }}
                    disabled={checkoutMutation.isPending}
                    className="mt-4 w-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-2 text-sm font-medium text-white shadow-md shadow-indigo-500/25 transition-all hover:shadow-lg hover:shadow-indigo-500/30 disabled:opacity-50"
                  >
                    {checkoutMutation.isPending ? "Loading..." : "Upgrade"}
                  </button>
                ) : (
                  <div className="mt-4 h-[36px]" />
                )}
              </div>
            );
          })}
        </div>
        {checkoutMutation.isError && (
          <div className="alert alert-error mt-4">
            <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            {checkoutMutation.error instanceof ApiError
              ? checkoutMutation.error.message
              : "Failed to start checkout"}
          </div>
        )}
      </div>
    </div>
  );
}
